/**
 * Module Block Viewer - Learner View
 * Read-only display of module content blocks
 */
import React, { useState, useEffect } from 'react';
import toast from '../utils/toast';
import { SkeletonQuiz, SkeletonBlock } from './Skeleton';
import ErrorCard from './ErrorCard';
import { retryFetchJSON } from '../utils/retry';

// Quiz Delivery Sub-Component
function QuizDelivery({ blockId }) {
  const [quiz, setQuiz] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    findQuizAndLoad();
  }, [blockId]);

  const findQuizAndLoad = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');

      // Try to get quiz_id by attempting to create a quiz for this block
      // If it already exists, the backend will return 400 with error
      // This is a workaround - in production you'd want GET /module-blocks/{id}/quiz
      const createResponse = await fetch(`/quizzes/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'Quiz',
          pass_mark: 70,
          max_attempts: 1,
          randomize_questions: false
        })
      });

      let foundQuizId = null;

      if (createResponse.ok) {
        // Quiz was just created (shouldn't happen in learner view, but handle it)
        const newQuiz = await createResponse.json();
        foundQuizId = newQuiz.id;
      } else if (createResponse.status === 400) {
        // Quiz already exists - try to infer quiz_id
        // For now, we'll try using blockId as quiz_id (this is a limitation)
        foundQuizId = blockId;
      } else {
        throw new Error('Failed to find quiz');
      }

      if (foundQuizId) {
        setQuizId(foundQuizId);
        await loadQuizData(foundQuizId);
      }
    } catch (err) {
      console.error('Error finding quiz:', err);
      setError(err.message || 'Failed to load quiz');
      setLoading(false);
    }
  };

  const loadQuizData = async (quizIdToLoad) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setMaxAttemptsReached(false);

    try {
      const token = localStorage.getItem('token');

      const quizResponse = await fetch(`/quizzes/${quizIdToLoad}/take`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (quizResponse.status === 403) {
        const errorData = await quizResponse.json();
        if (errorData.detail && errorData.detail.includes('Maximum attempts')) {
          setMaxAttemptsReached(true);
          // Fetch past attempts
          const attemptsResponse = await fetch(`/quizzes/${quizIdToLoad}/attempts`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (attemptsResponse.ok) {
            const attemptsData = await attemptsResponse.json();
            setAttempts(attemptsData);
          }
        }
        setLoading(false);
        return;
      }

      if (!quizResponse.ok) throw new Error('Failed to load quiz');

      const quizData = await quizResponse.json();
      setQuiz(quizData);

      // Initialize answers object
      const initialAnswers = {};
      quizData.questions.forEach(q => {
        initialAnswers[q.id] = [];
      });
      setAnswers(initialAnswers);

    } catch (err) {
      console.error('Error loading quiz:', err);
      setError(err.message || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, optionId, isMultiSelect) => {
    if (isMultiSelect) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: prev[questionId].includes(optionId)
          ? prev[questionId].filter(id => id !== optionId)
          : [...prev[questionId], optionId]
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: [optionId]
      }));
    }
  };

  const handleSubmit = async () => {
    if (!quizId) {
      setError('Quiz ID not found');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      const submission = {
        answers: Object.keys(answers).map(questionId => ({
          question_id: parseInt(questionId),
          selected_option_ids: answers[questionId]
        }))
      };

      const response = await fetch(`/quizzes/${quizId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submission)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit quiz');
      }

      const resultData = await response.json();
      setResult(resultData);

      // Show success/failure toast
      if (resultData.passed) {
        toast.success(`Quiz Passed! Score: ${resultData.score}%`);
      } else {
        toast.warning(`Quiz completed with ${resultData.score}%. Pass mark: ${resultData.pass_mark}%`);
      }

    } catch (err) {
      setError(err.message || 'Failed to submit quiz');
      toast.error(`Failed to submit quiz: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    if (quizId) {
      loadQuizData(quizId);
    }
  };

  if (loading) {
    return <SkeletonQuiz />;
  }

  if (error && !quiz) {
    return (
      <ErrorCard
        message={error}
        onRetry={findQuizAndLoad}
      />
    );
  }

  if (maxAttemptsReached) {
    return (
      <div style={{ padding: '32px', background: '#fff3cd', border: '2px solid #d4af37', borderRadius: '8px' }}>
        <div style={{ fontSize: '2rem', marginBottom: '12px', textAlign: 'center' }}></div>
        <h3 style={{ margin: '0 0 12px 0', color: '#856404', textAlign: 'center' }}>Maximum Attempts Reached</h3>
        <p style={{ margin: '0 0 16px 0', color: '#856404', textAlign: 'center' }}>
          You have used all available attempts for this quiz.
        </p>
        {attempts.length > 0 && (
          <div>
            <h4 style={{ margin: '16px 0 8px 0', color: '#856404' }}>Your Attempts:</h4>
            {attempts.map((attempt, idx) => (
              <div key={attempt.id} style={{ padding: '8px', background: '#fff', borderRadius: '4px', marginBottom: '8px' }}>
                Attempt {attempt.attempt_number}: {attempt.score}% - {attempt.passed ? 'Passed' : 'Failed'}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (result) {
    const attemptsRemaining = quiz && quiz.max_attempts ? quiz.max_attempts - result.attempt_number : 0;
    const canRetake = !result.passed && attemptsRemaining > 0;

    return (
      <div style={{ padding: '32px', background: result.passed ? '#e8f5e9' : '#ffebee', border: `2px solid ${result.passed ? '#4caf50' : '#e74c3c'}`, borderRadius: '8px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px', textAlign: 'center' }}>
          {result.passed ? ''  : '' }
        </div>
        <h3 style={{ margin: '0 0 12px 0', textAlign: 'center', color: result.passed ? '#2e7d32' : '#c62828' }}>
          {result.passed ? 'Quiz Passed!' : 'Quiz Failed'}
        </h3>
        <div style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: '700', marginBottom: '16px', color: result.passed ? '#2e7d32' : '#c62828' }}>
          Score: {result.score}%
        </div>
        <div style={{ textAlign: 'center', color: '#666', marginBottom: '8px' }}>
          You answered {result.correct_count} out of {result.total_questions} questions correctly.
        </div>
        <div style={{ textAlign: 'center', color: '#666', marginBottom: '8px' }}>
          Pass mark: {result.pass_mark}%
        </div>
        <div style={{ textAlign: 'center', color: '#666', marginBottom: canRetake ? '20px' : '8px' }}>
          Attempt {result.attempt_number} of {quiz.max_attempts}
        </div>

        {canRetake && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleRetake}
              style={{
                padding: '12px 32px',
                fontSize: '1rem',
                fontWeight: '700',
                background: '#d4af37',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retake Quiz ({attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining)
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!quiz) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', background: '#f9f9f9', borderRadius: '8px' }}>
        Quiz not available
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#fff', border: '2px solid #d4af37', borderRadius: '8px' }}>
      <h2 style={{ margin: '0 0 8px 0', color: '#d4af37' }}>{quiz.title}</h2>
      <div style={{ marginBottom: '24px', fontSize: '0.9rem', color: '#666' }}>
        Pass Mark: {quiz.pass_mark}% | Attempt {quiz.attempts_used + 1} of {quiz.max_attempts}
      </div>

      {quiz.questions.map((question, qIdx) => (
        <div key={question.id} style={{ marginBottom: '32px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
          <div style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05rem', color: '#1a1a1a' }}>
            Question {qIdx + 1}
          </div>
          <div style={{ marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
            {question.question_text}
          </div>

          <div>
            {question.options.map((option) => {
              const isMultiSelect = question.question_type === 'multi_select';
              const isSelected = answers[question.id]?.includes(option.id);

              return (
                <label
                  key={option.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '8px',
                    background: isSelected ? '#fff3cd' : '#fff',
                    border: `2px solid ${isSelected ? '#d4af37' : '#ddd'}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type={isMultiSelect ? 'checkbox' : 'radio'}
                    name={`question_${question.id}`}
                    checked={isSelected}
                    onChange={() => handleAnswerChange(question.id, option.id, isMultiSelect)}
                    style={{ marginRight: '12px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '1rem' }}>{option.option_text}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{
          padding: '14px 32px',
          fontSize: '1.1rem',
          fontWeight: '700',
          background: '#d4af37',
          color: '#1a1a1a',
          border: 'none',
          borderRadius: '4px',
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
          width: '100%'
        }}
      >
        {submitting ? 'Submitting...' : 'Submit Quiz'}
      </button>
    </div>
  );
}

// Assignment Delivery Sub-Component
function AssignmentDelivery({ blockId }) {
  const [assignment, setAssignment] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    findAssignmentAndLoad();
  }, [blockId]);

  const findAssignmentAndLoad = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');

      // Try to find assignment by attempting to create one
      // If it exists, backend returns 400 with "already exists" message
      const createResponse = await fetch(`/assignments/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'Assignment',
          instructions: '',
          allowed_file_types: ['.pdf'],
          max_file_size: 10485760,
          required: true
        })
      });

      let foundAssignmentId = null;

      if (createResponse.ok) {
        // Assignment was just created (shouldn't happen in learner view)
        const newAssignment = await createResponse.json();
        foundAssignmentId = newAssignment.id;
      } else if (createResponse.status === 400) {
        // Assignment already exists - use blockId as assignment_id
        foundAssignmentId = blockId;
      } else {
        throw new Error('Failed to find assignment');
      }

      if (foundAssignmentId) {
        setAssignmentId(foundAssignmentId);
        await loadAssignmentData(foundAssignmentId);
      }
    } catch (err) {
      console.error('Error finding assignment:', err);
      setError(err.message || 'Failed to load assignment');
      setLoading(false);
    }
  };

  const loadAssignmentData = async (assignmentIdToLoad) => {
    try {
      const token = localStorage.getItem('token');

      // Fetch assignment details
      const assignmentResponse = await fetch(`/assignments/${assignmentIdToLoad}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!assignmentResponse.ok) {
        throw new Error('Failed to load assignment details');
      }

      const assignmentData = await assignmentResponse.json();
      setAssignment(assignmentData);

      // Fetch submissions
      const submissionsResponse = await fetch(`/assignments/${assignmentIdToLoad}/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (submissionsResponse.ok) {
        const submissionsData = await submissionsResponse.json();
        setSubmissions(submissionsData);
      }

      // Fetch status
      const statusResponse = await fetch(`/assignments/${assignmentIdToLoad}/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setStatus(statusData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading assignment data:', err);
      setError(err.message || 'Failed to load assignment');
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (assignment && !assignment.allowed_file_types.includes(fileExt)) {
      toast.error(`File type ${fileExt} is not allowed. Allowed types: ${assignment.allowed_file_types.join(', ')}`);
      e.target.value = '';
      setSelectedFile(null);
      return;
    }

    // Validate file size
    if (assignment && file.size > assignment.max_file_size) {
      toast.error(`File size exceeds maximum allowed (${(assignment.max_file_size / 1048576).toFixed(2)} MB)`);
      e.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`/assignments/${assignmentId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit assignment');
      }

      const data = await response.json();
      toast.success('Assignment submitted successfully!');

      // Reload assignment data
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      await loadAssignmentData(assignmentId);
    } catch (err) {
      console.error('Error submitting assignment:', err);
      setError(err.message || 'Failed to submit assignment');
      toast.error(`Failed to submit assignment: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <SkeletonBlock />;
  }

  if (error) {
    return <div style={{ padding: '24px', background: '#ffebee', color: '#c62828', borderRadius: '4px' }}>Error: {error}</div>;
  }

  if (!assignment) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No assignment found</div>;
  }

  const latestSubmission = submissions.length > 0 ? submissions[0] : null;
  const isGraded = latestSubmission && latestSubmission.status === 'graded';

  return (
    <div style={{ padding: '24px' }}>
      {/* Header with gold accent */}
      <div style={{
        background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
        color: '#1a1a1a',
        padding: '20px 24px',
        borderRadius: '8px 8px 0 0',
        marginBottom: '24px',
        fontWeight: '700',
        fontSize: '1.3rem'
      }}>
        {assignment.title}
        {assignment.required && (
          <span style={{ marginLeft: '12px', fontSize: '0.85rem', background: '#1a1a1a', color: '#d4af37', padding: '4px 10px', borderRadius: '4px' }}>
            Required
          </span>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e0e0e0'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#333' }}>Instructions</h3>
        <p style={{ margin: 0, lineHeight: '1.6', color: '#555', whiteSpace: 'pre-wrap' }}>
          {assignment.instructions}
        </p>
      </div>

      {/* Requirements */}
      <div style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e0e0e0'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#333' }}>Submission Requirements</h3>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Allowed File Types</div>
            <div style={{ fontWeight: '600', color: '#d4af37' }}>
              {assignment.allowed_file_types.join(', ')}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Maximum File Size</div>
            <div style={{ fontWeight: '600', color: '#d4af37' }}>
              {(assignment.max_file_size / 1048576).toFixed(2)} MB
            </div>
          </div>
        </div>
      </div>

      {/* Status Indicator */}
      {status && (
        <div style={{
          background: isGraded ? '#e8f5e9' : (status.has_submitted ? '#fff3e0' : '#f5f5f5'),
          padding: '16px 24px',
          borderRadius: '8px',
          marginBottom: '24px',
          border: `2px solid ${isGraded ? '#4caf50' : (status.has_submitted ? '#ff9800' : '#e0e0e0')}`
        }}>
          <div style={{ fontWeight: '700', fontSize: '0.95rem', marginBottom: '8px', color: '#333' }}>
            Status: {isGraded ? 'Graded' : (status.has_submitted ? 'Submitted' : 'Not Submitted')}
          </div>
          {status.has_submitted && (
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              Latest submission: {new Date(status.submitted_at).toLocaleString()}
            </div>
          )}
          {isGraded && status.latest_grade !== null && (
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#4caf50', marginTop: '8px' }}>
              Grade: {status.latest_grade}/100
            </div>
          )}
        </div>
      )}

      {/* File Upload UI */}
      <div style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e0e0e0'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#333' }}>Submit Your Work</h3>
        <input
          id="file-input"
          type="file"
          onChange={handleFileChange}
          accept={assignment.allowed_file_types.join(',')}
          style={{
            display: 'block',
            width: '100%',
            padding: '12px',
            marginBottom: '16px',
            border: '2px dashed #d4af37',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.95rem'
          }}
        />
        {selectedFile && (
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '0.9rem',
            color: '#333'
          }}>
            Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1048576).toFixed(2)} MB)
          </div>
        )}
        <button
          onClick={handleSubmit}
          disabled={!selectedFile || submitting}
          style={{
            padding: '12px 32px',
            background: selectedFile && !submitting ? '#d4af37' : '#ccc',
            color: '#1a1a1a',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFile && !submitting ? 'pointer' : 'not-allowed',
            fontWeight: '700',
            fontSize: '1rem',
            width: '100%'
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Assignment'}
        </button>
        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Submission History */}
      {submissions.length > 0 && (
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#333' }}>Submission History</h3>
          {submissions.map((sub, index) => (
            <div
              key={sub.id}
              style={{
                padding: '16px',
                background: sub.status === 'graded' ? '#e8f5e9' : '#f5f5f5',
                borderRadius: '4px',
                marginBottom: index < submissions.length - 1 ? '12px' : 0,
                border: sub.status === 'graded' ? '1px solid #4caf50' : '1px solid #e0e0e0'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#333', marginBottom: '4px' }}>
                    {sub.file_path.split('/').pop()}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    Submitted: {new Date(sub.submitted_at).toLocaleString()}
                  </div>
                </div>
                <div style={{
                  padding: '4px 12px',
                  background: sub.status === 'graded' ? '#4caf50' : '#ff9800',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>
                  {sub.status}
                </div>
              </div>
              {sub.status === 'graded' && sub.grade !== null && (
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '700',
                  color: '#4caf50',
                  marginTop: '8px'
                }}>
                  Grade: {sub.grade}/100
                </div>
              )}
              {sub.feedback && sub.feedback.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '8px', color: '#333' }}>
                    Instructor Feedback:
                  </div>
                  {sub.feedback.map((f, fIndex) => (
                    <div key={fIndex} style={{ marginBottom: fIndex < sub.feedback.length - 1 ? '8px' : 0 }}>
                      <div style={{ fontSize: '0.9rem', color: '#555', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {f.feedback_text}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px' }}>
                        {new Date(f.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModuleBlockViewer({ moduleId }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Inject fade-in animation CSS (only once)
  useEffect(() => {
    const styleId = 'block-fade-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes blockFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .block-fade-in {
          animation: blockFadeIn 0.4s ease-out forwards;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Scroll to top when module changes
  useEffect(() => {
    if (moduleId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchBlocks();
    }
  }, [moduleId]);

  // Track block views when blocks are loaded
  useEffect(() => {
    if (blocks.length > 0 && moduleId) {
      blocks.forEach(block => {
        trackBlockView(block.id);
      });
    }
  }, [blocks, moduleId]);

  const fetchBlocks = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/modules/${moduleId}/blocks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 403) {
        setAccessDenied(true);
        setBlocks([]);
        setError(null);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch module content');
      }

      const data = await response.json();
      setBlocks(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setError(err.message || 'Failed to load module content');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  const trackBlockView = async (blockId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/modules/${moduleId}/track-view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ block_id: blockId })
      });
    } catch (err) {
      // Silently fail - tracking is non-critical
      console.debug('Failed to track block view:', err);
    }
  };

  const renderTextBlock = (block) => {
    const content = block.data?.content || '';
    const paragraphs = content.split('\n').filter(p => p.trim());

    return (
      <div style={blockContainerStyle}>
        <div style={blockHeaderStyle}>
          <div style={blockHeaderIconStyle}></div>
          <div style={blockHeaderTextStyle}>Text Content</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', borderRadius: '0 0 8px 8px' }}>
          {paragraphs.map((paragraph, index) => (
            <p key={index} style={paragraphStyle}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    );
  };

  const renderImageBlock = (block) => {
    const { url, caption, alt } = block.data || {};

    if (!url) return null;

    return (
      <div style={blockContainerStyle}>
        <div style={blockHeaderStyle}>
          <div style={blockHeaderIconStyle}></div>
          <div style={blockHeaderTextStyle}>Image</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', borderRadius: '0 0 8px 8px' }}>
          <img
            src={url}
            alt={alt || caption || 'Image'}
            style={imageStyle}
          />
          {caption && (
            <p style={captionStyle}>{caption}</p>
          )}
        </div>
      </div>
    );
  };

  const renderVideoBlock = (block) => {
    const { url, provider } = block.data || {};

    if (!url) return null;

    let videoElement;

    if (provider === 'youtube') {
      // Extract YouTube video ID
      let videoId = url;
      const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
      if (youtubeMatch) {
        videoId = youtubeMatch[1];
      }

      videoElement = (
        <iframe
          width="100%"
          height="450"
          src={`https://www.youtube.com/embed/${videoId}`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={iframeStyle}
        />
      );
    } else if (provider === 'vimeo') {
      // Extract Vimeo video ID
      let videoId = url;
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        videoId = vimeoMatch[1];
      }

      videoElement = (
        <iframe
          width="100%"
          height="450"
          src={`https://player.vimeo.com/video/${videoId}`}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={iframeStyle}
        />
      );
    } else {
      // MP4 or direct video link
      videoElement = (
        <video controls style={videoStyle}>
          <source src={url} type="video/mp4" />
          Your browser does not support the video element.
        </video>
      );
    }

    return (
      <div style={blockContainerStyle}>
        <div style={blockHeaderStyle}>
          <div style={blockHeaderIconStyle}></div>
          <div style={blockHeaderTextStyle}>Video</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', borderRadius: '0 0 8px 8px' }}>
          {videoElement}
        </div>
      </div>
    );
  };

  const renderFileBlock = (block) => {
    const { url, filename } = block.data || {};

    if (!url) return null;

    return (
      <div style={blockContainerStyle}>
        <div style={blockHeaderStyle}>
          <div style={blockHeaderIconStyle}></div>
          <div style={blockHeaderTextStyle}>File Download</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', borderRadius: '0 0 8px 8px' }}>
          <a
            href={url}
            download
            style={downloadLinkStyle}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download: {filename || 'File'}
          </a>
        </div>
      </div>
    );
  };

  const renderSectionBlock = (block) => {
    const { title } = block.data || {};

    if (!title) return null;

    return (
      <div style={blockContainerStyle}>
        <h2 style={sectionHeadingStyle}>
          {title}
        </h2>
      </div>
    );
  };

  const renderQuizBlock = (block) => {
    return <QuizDelivery blockId={block.id} />;
  };

  const renderAssignmentBlock = (block) => {
    return <AssignmentDelivery blockId={block.id} />;
  };

  // SCORM Player Component with Runtime Tracking
  function ScormPlayer({ blockData }) {
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const { package_title, sco_title, launch_path, scorm_package_id, sco_identifier } = blockData || {};

    useEffect(() => {
      // Load SCORM runtime script
      const script = document.createElement('script');
      script.src = '/scormRuntime.js';
      script.async = true;
      script.onload = () => {
        console.log('[SCORM Player] Runtime script loaded');

        // Configure SCORM API
        if (window.API && window.API.configure) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const token = localStorage.getItem('token');

          window.API.configure({
            packageId: scorm_package_id,
            scoIdentifier: sco_identifier,
            authToken: token,
            userId: user.id,
            userName: user.full_name || user.username
          });

          console.log('[SCORM Player] API configured');
          toast.success('SCORM API Connected - Progress tracking active');
        }

        setScriptLoaded(true);
      };

      script.onerror = () => {
        console.error('[SCORM Player] Failed to load runtime script');
        toast.error('Failed to load SCORM runtime. Progress may not be tracked.');
        setScriptLoaded(true); // Still show content
      };

      document.head.appendChild(script);

      // Cleanup on unmount
      return () => {
        // Call LMSFinish if initialized
        if (window.API && window.API.LMSFinish) {
          try {
            window.API.LMSFinish('');
          } catch (e) {
            console.error('[SCORM Player] Error calling LMSFinish on unmount:', e);
          }
        }

        // Remove script
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }, [scorm_package_id, sco_identifier]);

    if (!launch_path || !scorm_package_id) {
      return (
        <div style={{
          padding: '24px',
          background: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          border: '2px solid #e57373'
        }}>
          Error: Missing SCORM content information
        </div>
      );
    }

    // Construct SCORM content URL
    const scormUrl = `/uploads/scorm/${scorm_package_id}/content/${launch_path}`;

    return (
      <div style={{ marginBottom: '24px' }}>
        {/* SCORM Header */}
        <div style={{
          background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
          color: '#1a1a1a',
          padding: '16px 24px',
          borderRadius: '8px 8px 0 0',
          marginBottom: '0',
          fontWeight: '700',
          fontSize: '1.2rem'
        }}>
          {sco_title || 'SCORM Content'}
        </div>

        {/* SCORM Info */}
        <div style={{
          background: '#fff9e6',
          padding: '12px 24px',
          borderLeft: '4px solid #d4af37',
          marginBottom: '0',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <strong>Package:</strong> {package_title || 'Unknown'}
          {scriptLoaded && (
            <span style={{ marginLeft: '16px', color: '#4caf50', fontWeight: '600' }}>
              SCORM API Active
            </span>
          )}
        </div>

        {/* SCORM Content Iframe */}
        <div style={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          background: '#000',
          borderRadius: '0 0 8px 8px',
          overflow: 'hidden'
        }}>
          <iframe
            src={scormUrl}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={sco_title || 'SCORM Content'}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Info Note */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#e8f5e9',
          borderRadius: '4px',
          fontSize: '0.85rem',
          color: '#2e7d32',
          border: '1px solid #4caf50'
        }}>
          <strong>SCORM 1.2 Runtime Active:</strong> Your progress is being tracked automatically.
        </div>
      </div>
    );
  }

  const renderScormBlock = (block) => {
    return <ScormPlayer blockData={block.data} />;
  };

  const renderAudioBlock = (block) => {
    const { url, transcript } = block.data || {};

    if (!url) return null;

    return (
      <div style={blockContainerStyle}>
        <div style={blockHeaderStyle}>
          <div style={blockHeaderIconStyle}></div>
          <div style={blockHeaderTextStyle}>Audio</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', borderRadius: '0 0 8px 8px' }}>
          <audio controls style={audioStyle}>
            <source src={url} />
            Your browser does not support the audio element.
          </audio>
          {transcript && (
            <div style={transcriptStyle}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#d4af37' }}>
                Transcript:
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
                {transcript}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBlock = (block) => {
    switch (block.block_type) {
      case 'text':
        return renderTextBlock(block);
      case 'image':
        return renderImageBlock(block);
      case 'video':
        return renderVideoBlock(block);
      case 'file':
        return renderFileBlock(block);
      case 'section':
        return renderSectionBlock(block);
      case 'quiz':
        return renderQuizBlock(block);
      case 'assignment':
        return renderAssignmentBlock(block);
      case 'scorm':
        return renderScormBlock(block);
      case 'audio':
        return renderAudioBlock(block);
      default:
        return null;
    }
  };

  // Styles
  const containerStyle = {
    padding: '24px 0'
  };

  const blockContainerStyle = {
    marginBottom: '24px'
  };

  const blockHeaderStyle = {
    background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
    color: '#1a1a1a',
    padding: '12px 20px',
    borderRadius: '8px 8px 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: '700',
    fontSize: '1rem'
  };

  const blockHeaderIconStyle = {
    fontSize: '1.3rem'
  };

  const blockHeaderTextStyle = {
    fontSize: '1rem',
    fontWeight: '700'
  };

  const paragraphStyle = {
    margin: '0 0 16px 0',
    fontSize: '1rem',
    lineHeight: '1.7',
    color: '#333'
  };

  const imageStyle = {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    display: 'block'
  };

  const captionStyle = {
    margin: '12px 0 0 0',
    fontSize: '0.9rem',
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center'
  };

  const iframeStyle = {
    borderRadius: '8px',
    maxWidth: '100%'
  };

  const videoStyle = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: '8px'
  };

  const downloadLinkStyle = {
    display: 'inline-block',
    padding: '12px 24px',
    background: '#d4af37',
    color: '#1a1a1a',
    textDecoration: 'none',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'opacity 0.2s'
  };

  const sectionHeadingStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#d4af37',
    margin: '32px 0 16px 0',
    paddingBottom: '8px',
    borderBottom: '3px solid #d4af37'
  };

  const quizPlaceholderStyle = {
    padding: '32px',
    background: '#f9f9f9',
    border: '2px dashed #d4af37',
    borderRadius: '8px',
    textAlign: 'center',
    color: '#1a1a1a'
  };

  const audioStyle = {
    width: '100%',
    maxWidth: '100%',
    marginBottom: '16px'
  };

  const transcriptStyle = {
    padding: '16px',
    background: '#f9f9f9',
    borderRadius: '4px',
    borderLeft: '4px solid #d4af37'
  };

  const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    background: '#f9f9f9',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    color: '#666'
  };

  const errorStyle = {
    padding: '16px',
    background: '#ffebee',
    border: '2px solid #e74c3c',
    borderRadius: '4px',
    color: '#c62828',
    marginBottom: '24px'
  };

  const accessDeniedStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    background: '#fff3cd',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    color: '#856404'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <p style={{ margin: 0, fontSize: '1rem' }}>Loading module content...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={containerStyle}>
        <div style={accessDeniedStyle}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}></div>
          <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            You do not have access to this module.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          {error}
        </div>
      </div>
    );
  }

  if (blocks.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            This module has no content yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {blocks.map((block, index) => (
        <div
          key={block.id}
          className="block-fade-in"
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          {renderBlock(block)}
        </div>
      ))}
    </div>
  );
}

export default ModuleBlockViewer;
